import { Injectable } from '@angular/core';
import { ContentSlot, Page, productListTemplateSlots } from '@boels/models';
import { layoutConfig } from './layout.config';

@Injectable({
  providedIn: 'root',
})
export class CmsService {
  public getMainLayoutSlots(page: Page): ContentSlot[] | undefined {
    const layoutSlots = this.getLayoutConfigMainSlots(page);
    return layoutSlots && this.getSortedCmsSlots(page, layoutSlots);
  }

  public getTopLayoutSlots(page: Page): ContentSlot[] | undefined {
    const layoutSlots = this.getLayoutConfigTopSlots(page);
    return layoutSlots && this.getSortedCmsSlots(page, layoutSlots);
  }

  public getBottomLayoutSlots(page: Page): ContentSlot[] | undefined {
    const layoutSlots = this.getLayoutConfigBottomSlots(page);
    return layoutSlots && this.getSortedCmsSlots(page, layoutSlots);
  }

  public getSlotByPosition(
    page: Page,
    position: string,
  ): ContentSlot | undefined {
    return page.contentSlots?.contentSlot?.find(
      (slot) => slot.position === position,
    );
  }

  public getComponentData(
    contentSlot: ContentSlot,
    uid: string,
  ): ContentSlot | undefined {
    return contentSlot.components?.component?.find(
      (component) => component.uid === uid,
    );
  }

  protected getLayoutConfigMainSlots(page: Page): string[] {
    const pageTemplate: string = page?.template ?? '';
    return layoutConfig[pageTemplate]?.slots;
  }

  protected getLayoutConfigTopSlots(page: Page): string[] | undefined {
    const pageTemplate: string | undefined = page?.template ?? '';
    return layoutConfig[pageTemplate]?.topSlots;
  }

  protected getLayoutConfigBottomSlots(page: Page): string[] | undefined {
    const pageTemplate: string | undefined = page?.template ?? '';
    return layoutConfig[pageTemplate]?.bottomSlots;
  }

  protected getSortedCmsSlots(
    page: Page,
    layoutSlots: string[],
  ): ContentSlot[] | undefined {
    return (
      layoutSlots &&
      this.getLayoutCmsSlots(page, layoutSlots)?.sort(
        (a: ContentSlot, b: ContentSlot): number => {
          return this._sortByLayoutOrder(a, b, layoutSlots);
        },
      )
    );
  }

  protected getLayoutCmsSlots(
    page: Page,
    layoutSlots: string[],
  ): ContentSlot[] | undefined {
    return page.contentSlots?.contentSlot?.filter((slot) => {
      if (slot.slotId) {
        if (page.template === 'ProductListPageTemplate') {
          return layoutSlots.includes(
            this.getProductListTemplateSlotLocalName(slot),
          );
        }

        return layoutSlots.includes(this.getSlotLocalName(slot));
      }

      return false;
    });
  }

  protected getSlotLocalName(slot: ContentSlot): string {
    return slot?.position ?? slot.slotId.split('-')?.[0]?.replace('Slot', '');
  }

  protected getProductListTemplateSlotLocalName(slot: ContentSlot): string {
    return productListTemplateSlots[slot.slotId];
  }

  protected _sortByLayoutOrder(
    a: ContentSlot,
    b: ContentSlot,
    templateSlots: string[],
  ): number {
    return (
      this.getTemplateSlotIndex(templateSlots, a) -
      this.getTemplateSlotIndex(templateSlots, b)
    );
  }

  protected getTemplateSlotIndex(templateSlots: string[], item: ContentSlot) {
    return templateSlots.indexOf(this.getSlotLocalName(item));
  }

  public getFoldBreakpointSlot(page: Page): ContentSlot | undefined {
    const pageTemplate = page.template;
    if (pageTemplate) {
      const template = Object.fromEntries(
        Object.entries(layoutConfig).filter(([key]) => key === pageTemplate),
      )[pageTemplate];
      return this.getMainLayoutSlots(page)?.find(
        (item: ContentSlot) => item && item.position === template.pageFold,
      );
    }
    return;
  }
}
